import { useState } from "react";
import api from "../../api/client";
import { CALL_STATUS_OPTIONS, CALL_STATUS_OTHERS, getCallStatusColor } from "../../utils/callStatusOptions";

/* ─── Read-only record context shown at top ─── */
function RecordInfo({ name, address, label, value }) {
  return (
    <div className="bg-gray-50 border rounded-lg px-3 py-2.5 mb-4 space-y-1">
      <p className="text-sm font-semibold text-gray-900" title={name}>{name || "—"}</p>
      <p className="text-xs text-gray-500">{address || "—"}</p>
      <p className="text-xs text-gray-500"><span className="font-medium text-gray-600">{label}:</span> {value || "—"}</p>
    </div>
  );
}

/** All NI call status options including "Others..." */
const NI_CALL_OPTIONS = [...CALL_STATUS_OPTIONS, CALL_STATUS_OTHERS];

const CALL_CANCEL_REASONS = ["Cancelled by subs", "Full NAP", "Other reason"];

/**
 * Quick Call Status dialog for NI job orders.
 */
export function QuickCallStatusDialog({ jo, selectedStatus, onDone, onCancel }) {
  const isOthers = selectedStatus === CALL_STATUS_OTHERS;
  const isRescheduled = selectedStatus === "Rescheduled";
  const isCancelled = selectedStatus === "Cancelled";

  const [customText, setCustomText] = useState("");
  const [remarks, setRemarks] = useState("");
  const [newDate, setNewDate] = useState("");
  const [cancelReasons, setCancelReasons] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const toggleReason = (reason) =>
    setCancelReasons((prev) => prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]);

  const finalStatus = isOthers ? customText.trim() : selectedStatus;

  const canConfirm = (() => {
    if (!remarks.trim()) return false;
    if (isOthers && !customText.trim()) return false;
    if (isRescheduled && !newDate) return false;
    if (isCancelled && cancelReasons.length === 0) return false;
    return true;
  })();

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setError("");
    try {
      if (isRescheduled) {
        // 1) Set call status + remarks via PUT
        await api.put(`/job-orders/${jo.id}`, {
          call_status: "Rescheduled",
          call_status_remarks: remarks.trim(),
        });
        // 2) Reschedule the JO via POST /reschedule
        await api.post(`/job-orders/${jo.id}/reschedule`, {
          new_job_date: newDate,
          jo_status_remarks: `Rescheduled as per subs - ${remarks.trim()}`,
          team_number: jo.team_number || null,
          personnel_names: jo.personnel_names || null,
        });
      } else if (isCancelled) {
        // Build combined remark with reasons
        const reasonPrefix = cancelReasons.join("; ");
        const callRemark = remarks.trim();
        const combinedRemark = `${reasonPrefix} - ${callRemark}`;
        // PUT sets call_status to Cancelled → backend auto-cancels JO
        await api.put(`/job-orders/${jo.id}`, {
          call_status: "Cancelled",
          call_status_remarks: combinedRemark,
        });
      } else {
        // Default: just update call_status + remarks
        await api.put(`/job-orders/${jo.id}`, {
          call_status: finalStatus,
          call_status_remarks: remarks.trim(),
        });
      }
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update call status.");
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor = isOthers ? "bg-purple-100 text-purple-800" : getCallStatusColor(selectedStatus);

  // Compute min date for reschedule (tomorrow)
  const tomorrow = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  })();

  // Max date for reschedule: ageing_origin_date + 10 days
  const maxDate = (() => {
    if (!jo.ageing_origin_date) return "";
    const d = new Date(jo.ageing_origin_date);
    d.setDate(d.getDate() + 10);
    return d.toISOString().split("T")[0];
  })();

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-2">Update Call Status</h3>
        <p className="text-sm text-gray-600 mb-3">
          Set call status to{" "}
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
            {isOthers ? "Others" : selectedStatus}
          </span>
        </p>
        <RecordInfo name={jo.customer_full_name} address={jo.complete_address} label="JO Number" value={jo.jo_number} />

        {isOthers && (
          <>
            <label className="block text-sm font-medium mb-1">Custom Call Status <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="Enter custom call status…"
              className="w-full border rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </>
        )}

        {isRescheduled && (
          <>
            <label className="block text-sm font-medium mb-1">New Date <span className="text-red-500">*</span></label>
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              min={tomorrow}
              max={maxDate || undefined}
              className="w-full border rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </>
        )}

        <label className="block text-sm font-medium mb-1">Call Remarks <span className="text-red-500">*</span></label>
        <textarea
          rows={3}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Enter call remarks…"
          className="w-full border rounded px-3 py-2 text-sm mb-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {isCancelled && (
          <>
            <label className="block text-sm font-medium mb-1">Cancellation Reasons <span className="text-red-500">*</span></label>
            <div className="space-y-1.5 mb-3">
              {CALL_CANCEL_REASONS.map((reason) => (
                <label key={reason} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                  <input type="checkbox" checked={cancelReasons.includes(reason)} onChange={() => toggleReason(reason)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  {reason}
                </label>
              ))}
            </div>
            <p className="text-xs text-amber-600 mb-3">
              Setting call status to Cancelled will also cancel this job order.
            </p>
          </>
        )}

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
          <button
            type="button"
            disabled={!canConfirm || submitting}
            onClick={handleConfirm}
            className="px-4 py-2 text-sm text-white rounded disabled:opacity-50 bg-blue-600 hover:bg-blue-700"
          >
            {submitting ? "Saving…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The menu options list for NI call status quick action */
export { NI_CALL_OPTIONS };
